/**
 * Customer statement (spec §2.1, README M.1 "Send statement"): opening
 * balance, the period's invoices, payments and credit notes with a running
 * balance, and the closing balance, with the server's PDF and share link.
 * Sharing goes through the shop's own WhatsApp or email app; statements have
 * no document timeline, so nothing is logged.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import InvoiceRepository, { Statement } from '../../../internals/repository/InvoiceRepository';
import { errorText } from '../detailModel';
import { date, money, todayColombo } from '../format';
import { mailtoUrl, toE164, waUrl } from '../shareLinks';
import {
  emailSubject, ENTRY_LABEL, PERIOD_PRESETS, PeriodPreset, periodFor, periodLabel, reconcile, shareMessage,
} from '../statementModel';
import { useSelectedShop } from '../useInvoicing';
import { flashToast } from './MessageComposer';
import { Dialog, ErrorBanner } from './VoidModal';

const NOT_SAVED = 'Statements are available for customers saved in Customers.';

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  }
}

export interface StatementDialogProps {
  open: boolean;
  onClose: () => void;
  shopId: string;
  clientId: string;
  customerName: string;
  /** From the client record; WhatsApp opens without a number when absent. */
  phone?: string | null;
  email?: string | null;
}

export default function StatementDialog(props: StatementDialogProps) {
  if (!props.open) return null;
  return <StatementBody {...props} />;
}

function StatementBody({
  onClose, shopId, clientId, customerName, phone, email,
}: StatementDialogProps) {
  const shop = useSelectedShop();
  const currency = shop?.currency || 'LKR';
  const shopName = shop?.shopName || '';
  const today = todayColombo();

  const [preset, setPreset] = useState<PeriodPreset>('THIS_MONTH');
  const [custom, setCustom] = useState(() => periodFor('THIS_MONTH', today)!);
  const period = useMemo(() => periodFor(preset, today, custom), [preset, today, custom]);

  const [data, setData] = useState<Statement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const load = useCallback(async (p: { from: string; to: string }) => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await InvoiceRepository.statement(shopId, clientId, p.from, p.to);
      if (mine === seq.current) setData(res);
    } catch (e: any) {
      if (mine !== seq.current) return;
      setData(null);
      setError(e?.status === 404 || e?.code === 'not_found' ? NOT_SAVED : errorText(e));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [shopId, clientId]);

  useEffect(() => {
    if (period) load(period);
  }, [period?.from, period?.to, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const label = periodLabel(data?.from ?? period?.from, data?.to ?? period?.to);
  const name = data?.customer?.name || customerName;
  const link = data?.shareUrl || '';
  const message = link ? shareMessage(name, shopName, label, link) : '';
  const check = useMemo(() => (data ? reconcile(data) : null), [data]);
  const ready = !!data && !loading;
  const customBad = preset === 'CUSTOM' && !period;

  const onCopy = async () => {
    if (!link) return;
    if (await copyText(link)) flashToast('Statement link copied');
    else flashToast('Could not copy the link. Try again.', 'warn');
  };

  const linkBtn = (enabled: boolean) => `nl-btn nl-btn--secondary nl-btn--sm${enabled ? '' : ' pointer-events-none opacity-50'}`;

  return (
    <Dialog
      width={800}
      title="Customer statement"
      sub={(
        <>
          <span className="font-semibold text-text1">{name}</span>
          {data?.customer?.companyName && <span> · {data.customer.companyName}</span>}
          <span> · {label}</span>
        </>
      )}
      onClose={onClose}
      footer={(
        <>
          {ready && !data!.pdfUrl && (
            <span className="mr-auto text-[12px] text-text3">The PDF is not ready for this statement yet.</span>
          )}
          {ready && data!.pdfUrl ? (
            <a className={linkBtn(true)} href={data!.pdfUrl} target="_blank" rel="noopener noreferrer">Download PDF</a>
          ) : (
            <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm" disabled>Download PDF</button>
          )}
          <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm" disabled={!ready || !link} onClick={onCopy}>Copy link</button>
          {ready && link ? (
            <>
              <a className={linkBtn(true)} href={waUrl(toE164(phone), message)} target="_blank" rel="noopener noreferrer">Share on WhatsApp</a>
              <a className={linkBtn(true)} href={mailtoUrl(email, emailSubject(shopName, label), message)}>Share by email app</a>
            </>
          ) : (
            <>
              <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm" disabled>Share on WhatsApp</button>
              <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm" disabled>Share by email app</button>
            </>
          )}
          <button type="button" className="nl-btn nl-btn--primary nl-btn--sm" onClick={onClose}>Close</button>
        </>
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div role="group" aria-label="Period" className="flex flex-wrap gap-0.5 rounded-lg border border-borderc bg-surface2 p-[3px]">
            {PERIOD_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                aria-pressed={preset === p.value}
                onClick={() => setPreset(p.value)}
                className={`h-[28px] rounded-md px-2.5 text-[12px] font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${preset === p.value ? 'bg-surface text-brand shadow-sm' : 'text-text2'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'CUSTOM' && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1 text-[12px] font-semibold text-text2">
                From
                <input type="date" className="nl-input" value={custom.from} max={today} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
              </label>
              <label className="flex flex-col gap-1 text-[12px] font-semibold text-text2">
                To
                <input type="date" className="nl-input" value={custom.to} max={today} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
              </label>
            </div>
          )}
        </div>
        {customBad && <span className="text-[12px] font-medium" style={{ color: 'var(--negFg)' }}>! Choose a From date on or before the To date.</span>}

        <ErrorBanner text={error}>
          {error && error !== NOT_SAVED && period && (
            <button type="button" className="ml-2 font-semibold underline" onClick={() => load(period)}>Try again</button>
          )}
        </ErrorBanner>

        {loading && !data && (
          <div className="flex flex-col gap-2" aria-busy="true" aria-label="Loading statement">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-9 animate-pulse rounded-md bg-surface2" />)}
          </div>
        )}

        {data && (
          <div className={`flex flex-col gap-3 ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
            {check && !check.ok && (
              <div role="alert" className="rounded-lg border border-warn/30 bg-warn-soft px-3.5 py-2.5 text-[13px]" style={{ color: 'var(--warnFg)' }}>
                These figures do not add up (opening plus charges less payments gives {money(check.expectedClosingMinor, currency)}). Check the invoices before sending this statement.
              </div>
            )}

            <div className="flex items-center justify-between rounded-lg border border-borderc bg-surface2 px-3.5 py-2.5 text-[13px]">
              <span className="text-text2">Opening balance on {date(data.from)}</span>
              <span className="nl-num font-semibold text-text1">{money(data.openingBalanceMinor, currency)}</span>
            </div>

            <div className="overflow-x-auto rounded-lg border border-borderc">
              <table className="w-full min-w-[620px] text-[13px]">
                <thead>
                  <tr className="bg-surface2 text-left text-[11px] font-semibold tracking-[.06em] text-text3">
                    <th className="w-[110px] px-3.5 py-2 font-semibold">DATE</th>
                    <th className="px-3.5 py-2 font-semibold">DESCRIPTION</th>
                    <th className="w-[120px] px-3.5 py-2 text-right font-semibold">DEBIT</th>
                    <th className="w-[120px] px-3.5 py-2 text-right font-semibold">CREDIT</th>
                    <th className="w-[130px] px-3.5 py-2 text-right font-semibold">BALANCE</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.length === 0 && (
                    <tr className="border-t border-borderc">
                      <td colSpan={5} className="px-3.5 py-8 text-center text-text3">No invoices in this period</td>
                    </tr>
                  )}
                  {data.entries.map((e, i) => (
                    <tr key={`${e.docId}-${i}`} className="border-t border-borderc align-top">
                      <td className="whitespace-nowrap px-3.5 py-2.5 text-text2">{date(e.date)}</td>
                      <td className="px-3.5 py-2.5 text-text1">
                        <span>{ENTRY_LABEL[e.kind] ?? e.kind}</span>
                        {e.number && (
                          <>
                            {' '}
                            <Link to={`/invoices/${encodeURIComponent(e.docId)}`} className="nl-num font-semibold text-brand hover:underline">{e.number}</Link>
                          </>
                        )}
                        {e.description && <div className="text-[12px] text-text3">{e.description}</div>}
                      </td>
                      <td className="nl-num px-3.5 py-2.5 text-right text-text1">{e.debitMinor ? money(e.debitMinor, currency) : ''}</td>
                      <td className="nl-num px-3.5 py-2.5 text-right" style={{ color: e.creditMinor ? 'var(--posFg)' : undefined }}>{e.creditMinor ? money(e.creditMinor, currency) : ''}</td>
                      <td
                        className="nl-num px-3.5 py-2.5 text-right font-semibold text-text1"
                        style={check && check.firstBadIndex === i ? { color: 'var(--warnFg)' } : undefined}
                      >
                        {money(e.balanceMinor, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-brand-soft px-4 py-3">
              <div className="flex flex-col">
                <span className="text-[13px] font-semibold text-text1">Closing balance on {date(data.to)}</span>
                {data.totals && (
                  <span className="text-[12px] text-text2">
                    Invoiced {money(data.totals.invoicedMinor, currency)} · Paid {money(data.totals.paidMinor, currency)}
                    {data.totals.creditedMinor ? ` · Credited ${money(data.totals.creditedMinor, currency)}` : ''}
                  </span>
                )}
              </div>
              <span className="nl-num text-[18px] font-semibold text-brand">{money(data.closingBalanceMinor, currency)}</span>
            </div>

            {!link && <p className="m-0 text-[12px] text-text3">A share link is not available for this statement yet, so it cannot be shared.</p>}
          </div>
        )}
      </div>
    </Dialog>
  );
}
